import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTour } from "@/context/TourContext";
import { useTourTargetRect } from "@/lib/useTourTargetRect";

const CARD_WIDTH = 340;
const ESTIMATED_CARD_HEIGHT = 210;
const GAP = 16;
const VIEWPORT_MARGIN = 16;

// Module-level constants (not recreated every render) so framer-motion
// always receives stable object references for the parts of the
// animation that never change between steps.
const CARD_POSITION_TRANSITION = { duration: 0.22, ease: "easeOut" };
const STEP_FADE_INITIAL = { opacity: 0 };
const STEP_FADE_ANIMATE = { opacity: 1 };
const STEP_FADE_EXIT = { opacity: 0 };
const STEP_FADE_TRANSITION = { duration: 0.15 };

function computeCardPosition(rect) {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  // Responsive card width — never wider than the viewport allows, so the
  // callout can't clip off-screen on narrow phones (as low as ~320px).
  const cardWidth = Math.min(CARD_WIDTH, viewportW - VIEWPORT_MARGIN * 2);

  if (!rect) {
    return { position: "fixed", left: viewportW / 2 - cardWidth / 2, bottom: 48, width: cardWidth };
  }

  const spaceBelow = viewportH - (rect.top + rect.height);
  const placeBelow = spaceBelow >= ESTIMATED_CARD_HEIGHT + GAP;

  let left = rect.left + rect.width / 2 - cardWidth / 2;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, viewportW - cardWidth - VIEWPORT_MARGIN));

  let top;
  if (placeBelow) {
    top = rect.top + rect.height + GAP;
  } else if (rect.top - GAP - ESTIMATED_CARD_HEIGHT >= VIEWPORT_MARGIN) {
    top = rect.top - GAP - ESTIMATED_CARD_HEIGHT;
  } else {
    // Neither clearly fits above nor below (a small target near the
    // edge of the screen, e.g. the AI launcher FAB) — pin the card to a
    // safe on-screen band instead of letting it clip off-viewport.
    top = Math.max(VIEWPORT_MARGIN, Math.min(rect.top, viewportH - ESTIMATED_CARD_HEIGHT - VIEWPORT_MARGIN));
  }
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewportH - ESTIMATED_CARD_HEIGHT - VIEWPORT_MARGIN));

  return { position: "fixed", left, top, width: cardWidth };
}

/**
 * v1.3 Presentation Mode polish — the step callout card: counter,
 * title, "why this matters" body copy, and Previous / Next / Skip /
 * Finish controls. Positions itself relative to the current target
 * rect (or centers itself for a "concept" step with no target), and
 * announces step changes via an aria-live region for screen readers.
 */
export const TourStepPopover = () => {
  const { activeStep, stepIndex, steps, next, prev, skip, finish } = useTour();
  const rect = useTourTargetRect(activeStep?.targetTestId);
  const nextButtonRef = useRef(null);

  useEffect(() => {
    if (activeStep) nextButtonRef.current?.focus();
  }, [activeStep]);

  useEffect(() => {
    if (!activeStep) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") skip();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft" && stepIndex > 0) prev();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeStep, next, prev, skip, stepIndex]);

  if (!activeStep) return null;

  const isLast = stepIndex === steps.length - 1;
  const { position: _position, ...animatePosition } = computeCardPosition(rect);

  return (
    <motion.div
      animate={animatePosition}
      transition={CARD_POSITION_TRANSITION}
      style={{ position: "fixed", zIndex: 160 }}
      role="dialog"
      aria-label={`Guided tour, step ${stepIndex + 1} of ${steps.length}: ${activeStep.title}`}
      data-testid="tour-step-popover"
    >
      <div className="rounded-xl border bg-card shadow-xl p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium text-muted-foreground" data-testid="tour-step-counter">
            Step {stepIndex + 1} of {steps.length}
          </span>
          <button
            type="button"
            onClick={skip}
            className="rounded-sm p-1 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Skip tour"
            data-testid="tour-step-dismiss-button"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Only this inner block cross-fades between steps — the button
            row below stays mounted as the same DOM nodes across the
            whole tour, so a fast Next/Next/Next click sequence can never
            land on an element that's mid-unmount (the root cause of the
            occasional "element detachment" flake during automated
            testing). */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep.id}
            initial={STEP_FADE_INITIAL}
            animate={STEP_FADE_ANIMATE}
            exit={STEP_FADE_EXIT}
            transition={STEP_FADE_TRANSITION}
          >
            <h3 className="mt-1.5 font-heading text-sm font-semibold" data-testid="tour-step-title">{activeStep.title}</h3>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground" data-testid="tour-step-body">{activeStep.body}</p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={skip} data-testid="tour-skip-button">
            Skip Tour
          </Button>
          <div className="flex items-center gap-2">
            {stepIndex > 0 && (
              <Button variant="outline" size="sm" onClick={prev} data-testid="tour-previous-button">
                <ChevronLeft className="h-3.5 w-3.5" /> Previous
              </Button>
            )}
            <Button
              ref={nextButtonRef}
              size="sm"
              onClick={isLast ? finish : next}
              data-testid={isLast ? "tour-finish-button" : "tour-next-button"}
            >
              {isLast ? "Finish Tour" : "Next"}
              {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </div>
      <span className="sr-only" role="status" aria-live="polite">
        {`Step ${stepIndex + 1} of ${steps.length}: ${activeStep.title}`}
      </span>
    </motion.div>
  );
};
